import { Chip, GlobalStyles } from "@mui/material"
import React from "react"

import { Box, IconButton } from "@mui/material"
import { EditOutlined, DeleteOutlined } from "@mui/icons-material"
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
  getGridStringOperators,
} from "@mui/x-data-grid-pro"
import PropTypes from "prop-types"
import i18n from "i18n"
import DataGridLocaleText from "common/DataGridLocaleText"
import useLocalStorage from "../../hooks/useLocalStorage"
import useHideDisabledColumns from "hooks/useHideDisabledColumns"

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarQuickFilter
      quickFilterParser={searchInput =>
        searchInput.split(",").map(value => value.trim())
      }
      quickFilterFormatter={quickFilterValues => quickFilterValues.join(", ")}
    />
  </GridToolbarContainer>
)

const ContactsGrid = ({
  contacts,
  onEditContact,
  onDeleteContact,
  companies,
}) => {
  useHideDisabledColumns()
  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    "contacts-grid-column-visibility",
    {
      fullName: true,
      companyName: true,
      role: true,
      email: true,
      mobile: true,
      website: true,
      tagsDisplay: true,
      actions: true,
    },
  )

  const columns = [
    {
      field: "fullName",
      headerName: i18n.t("contacts.fullName"),
      width: 250,
      renderCell: params => <Box>{params.value || "-"}</Box>,
    },
    {
      field: "companyName",
      headerName: i18n.t("contacts.company"),
      width: 200,
      renderCell: params => <Box>{params.value || "-"}</Box>,
    },
    {
      field: "role",
      headerName: i18n.t("contacts.role"),
      width: 250,
      renderCell: params => <Box>{params.value || "-"}</Box>,
    },
    {
      field: "email",
      headerName: i18n.t("contacts.email"),
      width: 250,
      renderCell: params => <Box>{params.value || "-"}</Box>,
    },
    {
      field: "mobile",
      headerName: i18n.t("contacts.mobile"),
      width: 150,
      renderCell: params => <Box>{params.value || "-"}</Box>,
    },
    {
      field: "website",
      headerName: i18n.t("contacts.website"),
      width: 250,
      renderCell: params => <Box>{params.value || "-"}</Box>,
    },
    {
      field: "tagsDisplay",
      headerName: i18n.t("contacts.tags"),
      width: 300,
      sortable: true,
      filterable: true,
      type: "string",
      filterOperators: getGridStringOperators(),
      getQuickFilterText: params => {
        if (typeof params.value === "string" && params.value.length > 0) {
          return params.value
        }

        const tags = params.row?.tags
        if (!Array.isArray(tags) || tags.length === 0) return ""

        return tags.map(tag => tag.tag_name).join(" ")
      },
      getApplyQuickFilterFn: value => {
        if (!value) return null
        const needle = value.toString().toLowerCase().trim()

        if (!needle) return null

        return (cellValue, row) => {
          const tags = Array.isArray(row?.tags) ? row.tags : []

          if (tags.length === 0) {
            return false
          }

          return tags.some(tag => {
            if (!tag || typeof tag.tag_name !== "string") return false
            return tag.tag_name.toLowerCase().includes(needle)
          })
        }
      },
      renderCell: params => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {(params.row.tags || []).map((tag, idx) => (
            <Chip
              key={tag.tag_name + idx}
              label={tag.tag_name}
              size="small"
              sx={{
                backgroundColor: tag.tag_color,
                color: "#222",
                borderRadius: "4px",
                marginRight: 0.5,
              }}
            />
          ))}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: i18n.t("contacts.actions"),
      width: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      pinned: "right",
      hideable: false,
      renderCell: params => (
        <Box>
          <IconButton
            size="small"
            onClick={() => onEditContact?.(params.row)}
            title={i18n.t("contacts.edit")}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDeleteContact?.(params.row)}
            title={i18n.t("contacts.delete")}
          >
            <DeleteOutlined fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ width: "100%" }}>
      <GlobalStyles
        styles={{
          ".MuiFormControlLabel-root.Mui-disabled": {
            display: "none !important",
          },
        }}
      />
      <DataGridPro
        rows={
          contacts?.map(contact => {
            const companySK = contact.SK.split("#")[0]
            const company = companies?.find(c => c.SK === companySK)
            const tags = contact.tags || []

            return {
              ...contact,
              id: contact.SK,
              fullName:
                `${contact.title || ""} ${contact.firstname || ""} ${contact.lastname || ""}`.trim(),
              companyName: company ? company.companyName : "",
              tags,
              tagsDisplay: tags.map(tag => tag.tag_name).join(" "),
            }
          }) || []
        }
        columns={columns}
        getRowId={row => row.id}
        localeText={DataGridLocaleText()}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        pinnedColumns={{ right: ["actions"] }}
        slots={{ toolbar: CustomToolbar }}
        hideFooter
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        sx={{
          border: 0,
          "& .MuiDataGrid-toolbarContainer>Button": {
            color: "rgba(0, 0, 0, 0.87)",
          },
        }}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  )
}

ContactsGrid.propTypes = {
  contacts: PropTypes.array,
  onEditContact: PropTypes.func,
  onDeleteContact: PropTypes.func,
  companies: PropTypes.array,
}

ContactsGrid.defaultProps = {
  contacts: [],
  onEditContact: () => {},
  onDeleteContact: () => {},
  companies: [],
}

export default ContactsGrid
